import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeFrBe from '@angular/common/locales/fr-BE';
import localeNl from '@angular/common/locales/nl';
import localeNlBe from '@angular/common/locales/nl-BE';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { MetaReducer, StoreModule } from '@ngrx/store';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

import { BootstrapRoutingModule } from '@bootstrap/bootstrap-routing.module';
import { RootContainerComponent } from '@bootstrap/containers/root-container.component';
import { WorkflowApiInterceptor } from '@common/interceptors/workflow-api.interceptor';
import { MediahavenConfigService } from '@common/services/config/mediahaven-config.service';
import { MediaHavenStoreRecordsStateService } from '@common/services/store/store-record-state.service';
import { rootReducer } from '@reducers/root.reducer';

import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { AgmCoreModule, LAZY_MAPS_API_CONFIG } from '@duxor/agm-core';
import { APP_NAME_TOKEN, AppName, CustomTranslateLoader, LocaleProvider, SharedLanguageService } from '@language';
import {
    AdvancedSearchSandboxService,
    AggregateMiddleware,
    BaseSandbox,
    clearState,
    createLocalStorageSyncReducer,
    GeoSandboxService,
    HttpService,
    LoadingModule,
    LocalStoragePrefix,
    MH_STORE_KEYS,
    MhProfileConfigService,
    MHSearchRoutingService,
    PreviewDialogActionsService,
    RefreshRefreshTokenStrategy,
    RefreshShareTokenStrategy,
    SearchService,
    ShareApiInterceptor,
    SharedModule,
    ToastsModule,
    tokenInterceptorProviders,
    TrackingModule
} from '@mediahaven/shared';
import {
    AbstractGeoSandboxService,
    AbstractRecordsStateService,
    ApplicationConfigService,
    BaseAdvancedSearchSandbox,
    CACHE_CONFIG,
    ConfigService,
    CORE_APP_MODULES,
    EnvironmentConfigService,
    GoogleMapsConfig,
    PAGE_TITLE_CONFIG,
    RouterStateResolverService,
    ShAbstractRecordDownloadService,
    ShAbstractToastService,
    ShCoreAppModule,
    ShDynamicDialogService,
    ShProfileConfigService,
    ShRecordDownloadService,
    ShRecordService,
    ShSearchRoutingService,
    StylingService,
    ZonesService
} from '@stylehaven/components';
import { UploadsOverviewSandboxService } from '@uploads/sandbox/uploads-overview-sandbox.service';
import { MESSAGE_FORMAT_CONFIG, TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { MhPreviewDialogActionsService } from '../common/services/preview-dialog/preview-dialog-actions.service';

registerLocaleData(localeNlBe);
registerLocaleData(localeNl);
registerLocaleData(localeFrBe);
registerLocaleData(localeDe);

const metaReducers: MetaReducer<any>[] = [
    clearState,
    createLocalStorageSyncReducer({
        localStoragePrefix: LocalStoragePrefix.Synced,
        keys: MH_STORE_KEYS
    })
];

@NgModule({
    declarations: [RootContainerComponent],
    imports: [
        // Core App imports
        ...CORE_APP_MODULES,
        ShCoreAppModule.forRoot(HttpService),
        BootstrapRoutingModule,
        StoreModule.forRoot(rootReducer, { metaReducers }),

        // MH Shared imports
        SharedModule.forRoot(HttpService, AggregateMiddleware),

        // App specific imports
        AgmCoreModule.forRoot(),
        LoadingModule,
        ToastsModule,
        TrackingModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useClass: CustomTranslateLoader,
                deps: [APP_NAME_TOKEN]
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler
            }
        })
    ],
    providers: [
        // #region Translations and Locales
        {
            provide: APP_NAME_TOKEN,
            useFactory: (): AppName => {
                return 'mediahaven2';
            }
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (config: EnvironmentConfigService, translateService: SharedLanguageService) => () => {
                return config
                    .getConfig()
                    .pipe(map(config => translateService.init(config.fallbackLanguage)))
                    .toPromise();
            },
            deps: [EnvironmentConfigService, SharedLanguageService],
            multi: true
        },
        {
            provide: MESSAGE_FORMAT_CONFIG,
            useValue: {
                biDiSupport: true,
                formatters: { lowcase: (v: string) => v.toLowerCase() }
            }
        },
        LocaleProvider,
        // #endregion
        // #region Config
        {
            provide: EnvironmentConfigService,
            useClass: MediahavenConfigService
        },
        {
            provide: LAZY_MAPS_API_CONFIG,
            useClass: GoogleMapsConfig,
            deps: [EnvironmentConfigService]
        },
        // #endregion
        // #region Styling
        StylingService,
        // #endregion
        // #region Services
        {
            provide: APP_INITIALIZER,
            useFactory: (config: RouterStateResolverService) => () => {
                return true;
            },
            deps: [RouterStateResolverService],
            multi: true
        },
        {
            provide: AbstractRecordsStateService,
            useClass: MediaHavenStoreRecordsStateService
        },
        {
            provide: MHSearchRoutingService,
            useFactory: () => new MHSearchRoutingService('/all')
        },
        {
            provide: ShSearchRoutingService,
            useExisting: MHSearchRoutingService
        },
        {
            provide: ConfigService,
            useExisting: ApplicationConfigService
        },
        ShDynamicDialogService,
        ZonesService,
        { provide: ShProfileConfigService, useClass: MhProfileConfigService },
        { provide: AbstractGeoSandboxService, useClass: GeoSandboxService },
        { provide: PreviewDialogActionsService, useClass: MhPreviewDialogActionsService },
        {
            provide: BaseAdvancedSearchSandbox,
            useClass: AdvancedSearchSandboxService
        },
        // #endregion
        // #region Interceptors
        tokenInterceptorProviders,
        { provide: 'REFRESH_STRATEGIES', useClass: RefreshShareTokenStrategy, multi: true },
        { provide: 'REFRESH_STRATEGIES', useClass: RefreshRefreshTokenStrategy, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: WorkflowApiInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ShareApiInterceptor,
            multi: true
        },
        {
            provide: BaseSandbox,
            useExisting: RecordsSandbox
        },
        // #endregion
        {
            provide: CACHE_CONFIG,
            useValue: [
                { url: /profiles(\/|\?)/ },
                { url: /organisations(?:\/|\?|$)/ },
                { url: /classifications(?:\/|\?|$)/ },
                { url: /shares(?:\/|\?|$)/ }
            ]
        },
        {
            provide: PAGE_TITLE_CONFIG,
            useValue: {
                appTitle: 'MediaHaven',
                usePageHeaderSuffix: true
            }
        },
        UploadsOverviewSandboxService,
        {
            provide: ShAbstractRecordDownloadService,
            useClass: ShRecordDownloadService
        },
        {
            provide: ShAbstractToastService,
            useExisting: UploadsOverviewSandboxService // Any subclass of BaseSandbox will do
        },
        {
            provide: ShRecordService,
            useClass: SearchService
        }
    ],
    bootstrap: [RootContainerComponent],
    exports: [ShCoreAppModule]
})
export class BootstrapModule {}
